import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from '../../components/ui/button';
import FundingDocumentFilters from '../../components/funding/FundingDocumentFilters';
import FundingDocumentRequestModal from '../../components/funding/FundingDocumentRequestModal';
import FundingDocumentStatusBadge from '../../components/funding/FundingDocumentStatusBadge';
import FundingPanel from '../../components/funding/FundingPanel';
import FundingSectionPage from '../../components/funding/FundingSectionPage';
import { useFundingWorkspace } from '../../context/FundingWorkspaceContext';
import { useFundingDocuments } from '../../hooks/funding/useFundingDocuments';
import { fetchFundingClients } from '../../services/funding/fundingClientsService';

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const FundingDocumentsPage = () => {
  const {
    isPlatformContext,
    workspaceAgencyId,
    requiresAgencySelection,
  } = useFundingWorkspace();

  const [clients, setClients] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(true);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [requestOpen, setRequestOpen] = useState(false);
  const [actionError, setActionError] = useState(null);

  const listAgencyId = isPlatformContext ? workspaceAgencyId || undefined : workspaceAgencyId;
  const selectedClient = useMemo(
    () => clients.find((client) => client.id === selectedClientId) || null,
    [clients, selectedClientId],
  );

  const {
    documents,
    loading,
    saving,
    error,
    requestDocument,
    createChecklist,
    updateDocument,
    removeDocument,
    load,
  } = useFundingDocuments({
    agencyId: listAgencyId,
    fundingClientId: selectedClientId || undefined,
    status: statusFilter || undefined,
    category: categoryFilter || undefined,
    autoLoad: !isPlatformContext ? Boolean(workspaceAgencyId) : true,
  });

  const loadClients = useCallback(async () => {
    if (!isPlatformContext && !workspaceAgencyId) {
      setClients([]);
      setClientsLoading(false);
      return;
    }

    setClientsLoading(true);
    try {
      const result = await fetchFundingClients({ agencyId: listAgencyId });
      setClients(result.clients);
      if (!selectedClientId && result.clients.length > 0) {
        setSelectedClientId(result.clients[0].id);
      }
    } catch {
      setClients([]);
    } finally {
      setClientsLoading(false);
    }
  }, [isPlatformContext, workspaceAgencyId, listAgencyId, selectedClientId]);

  useEffect(() => {
    loadClients();
  }, [loadClients]);

  const filteredDocuments = useMemo(() => {
    if (!search.trim()) return documents;
    const term = search.trim().toLowerCase();
    return documents.filter((doc) => (
      (doc.clientName || '').toLowerCase().includes(term)
      || (doc.category || '').toLowerCase().includes(term)
      || (doc.fileName || '').toLowerCase().includes(term)
    ));
  }, [documents, search]);

  const handleRequest = async (payload) => {
    if (!selectedClient) return;
    setActionError(null);
    try {
      await requestDocument(selectedClient.id, payload);
      setRequestOpen(false);
    } catch (err) {
      setActionError(err?.message || 'Failed to request document');
    }
  };

  const handleStatusChange = async (doc, status) => {
    setActionError(null);
    try {
      const payload = { status };
      if (status === 'Uploaded' && !doc.fileName) {
        payload.file_name = `${doc.category.replace(/\s+/g, '_').toLowerCase()}.pdf`;
      }
      if (status === 'Rejected') {
        payload.rejection_reason = window.prompt('Rejection reason (optional)') || 'Needs correction';
      }
      await updateDocument(doc.id, payload);
    } catch (err) {
      setActionError(err?.message || 'Failed to update document');
    }
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`Delete ${doc.category}?`)) return;
    try {
      await removeDocument(doc.id);
    } catch (err) {
      setActionError(err?.message || 'Failed to delete document');
    }
  };

  const handleCreateChecklist = async () => {
    if (!selectedClient) return;
    setActionError(null);
    try {
      await createChecklist(selectedClient.id);
      await load();
    } catch (err) {
      setActionError(err?.message || 'Failed to create checklist');
    }
  };

  return (
    <FundingSectionPage
      title="Documents"
      subtitle="Track required funding documents per client and prepare packages for readiness and submission."
      activeStage="documents"
      actions={(
        <>
          <Button
            variant="outline"
            className="rounded-xl"
            onClick={handleCreateChecklist}
            disabled={!selectedClient || saving}
          >
            Create Default Checklist
          </Button>
          <Button
            className="rounded-xl bg-[var(--fg-accent)] hover:bg-[#126b4f]"
            onClick={() => setRequestOpen(true)}
            disabled={!selectedClient || saving}
          >
            Request Document
          </Button>
        </>
      )}
    >
      <FundingPanel title="Funding document tracker" subtitle="Per-client checklist and review workflow">
        <div className="space-y-4">
          <div className="grid gap-3 lg:grid-cols-[280px_1fr]">
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-[var(--fg-muted)]">
                Funding client
              </label>
              {clientsLoading ? (
                <p className="text-sm text-[var(--fg-muted)]">Loading clients...</p>
              ) : clients.length === 0 ? (
                <p className="text-sm text-[var(--fg-muted)]">
                  Create a funding client first, then build a document checklist here.
                </p>
              ) : (
                <select
                  value={selectedClientId}
                  onChange={(event) => setSelectedClientId(event.target.value)}
                  className="w-full rounded-xl border border-[var(--fg-line)] bg-white px-3 py-2.5 text-sm dark:bg-[rgba(11,31,24,0.65)]"
                >
                  {clients.map((client) => (
                    <option key={client.id} value={client.id}>
                      {client.name}
                      {client.documentStatus ? ` — ${client.documentStatus}` : ''}
                    </option>
                  ))}
                </select>
              )}
            </div>

            <FundingDocumentFilters
              search={search}
              status={statusFilter}
              category={categoryFilter}
              onSearchChange={setSearch}
              onStatusChange={setStatusFilter}
              onCategoryChange={setCategoryFilter}
            />
          </div>

          {requiresAgencySelection ? (
            <div className="rounded-xl border border-dashed border-[var(--fg-line)] bg-[var(--fg-panel-soft)] px-4 py-6 text-sm text-[var(--fg-muted)]">
              Select an agency in the header to manage funding documents.
            </div>
          ) : null}

          {(error || actionError) ? (
            <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-900/40 dark:bg-rose-950/30 dark:text-rose-200">
              {actionError || error}
            </div>
          ) : null}

          {loading ? (
            <p className="text-sm text-[var(--fg-muted)]">Loading documents...</p>
          ) : filteredDocuments.length === 0 ? (
            <div className="rounded-xl border border-dashed border-[var(--fg-line)] bg-[var(--fg-panel-soft)] px-4 py-10 text-center">
              <p className="text-sm font-medium text-[var(--fg-text)]">No funding documents yet</p>
              <p className="mt-1 text-sm text-[var(--fg-muted)]">
                Create a default checklist or request individual documents for the selected client.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-[var(--fg-line)] text-[var(--fg-muted)]">
                    <th className="px-3 py-2 font-medium">Client</th>
                    <th className="px-3 py-2 font-medium">Category</th>
                    <th className="px-3 py-2 font-medium">Status</th>
                    <th className="px-3 py-2 font-medium">File</th>
                    <th className="px-3 py-2 font-medium">Requested</th>
                    <th className="px-3 py-2 font-medium">Uploaded</th>
                    <th className="px-3 py-2 font-medium">Verified</th>
                    <th className="px-3 py-2 font-medium">Expires</th>
                    <th className="px-3 py-2 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDocuments.map((doc) => (
                    <tr key={doc.id} className="border-b border-[var(--fg-line)]/70">
                      <td className="px-3 py-3 text-[var(--fg-text)]">{doc.clientName || '—'}</td>
                      <td className="px-3 py-3 text-[var(--fg-text)]">{doc.category}</td>
                      <td className="px-3 py-3"><FundingDocumentStatusBadge status={doc.status} /></td>
                      <td className="px-3 py-3 text-[var(--fg-muted)]">
                        {doc.fileUrl ? (
                          <a href={doc.fileUrl} target="_blank" rel="noreferrer" className="text-[var(--fg-accent)] hover:underline">
                            {doc.fileName || 'View file'}
                          </a>
                        ) : (doc.fileName || '—')}
                      </td>
                      <td className="px-3 py-3 text-[var(--fg-muted)]">{formatDate(doc.requestedAt)}</td>
                      <td className="px-3 py-3 text-[var(--fg-muted)]">{formatDate(doc.uploadedAt)}</td>
                      <td className="px-3 py-3 text-[var(--fg-muted)]">{formatDate(doc.verifiedAt)}</td>
                      <td className="px-3 py-3 text-[var(--fg-muted)]">{formatDate(doc.expiresAt)}</td>
                      <td className="px-3 py-3">
                        <div className="flex flex-wrap gap-1">
                          {['Uploaded', 'In Review', 'Verified', 'Rejected'].map((status) => (
                            <button
                              key={status}
                              type="button"
                              disabled={saving}
                              onClick={() => handleStatusChange(doc, status)}
                              className="rounded-lg border border-[var(--fg-line)] px-2 py-1 text-[11px] font-medium text-[var(--fg-muted)] hover:bg-[var(--fg-accent-soft)] hover:text-[var(--fg-accent)]"
                            >
                              {status}
                            </button>
                          ))}
                          <button
                            type="button"
                            disabled={saving}
                            onClick={() => handleDelete(doc)}
                            className="rounded-lg border border-rose-200 px-2 py-1 text-[11px] font-medium text-rose-600 hover:bg-rose-50"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </FundingPanel>

      <FundingDocumentRequestModal
        open={requestOpen}
        onOpenChange={setRequestOpen}
        onSubmit={handleRequest}
        saving={saving}
        error={actionError}
      />
    </FundingSectionPage>
  );
};

export default FundingDocumentsPage;
